<script>
    import { createEventDispatcher, onMount, tick } from 'svelte';
    import { isOutOfViewport } from './utils.js';
    import OptionItem from './OptionItem.svelte';
    import {sgettext} from '../../i18n.js'

    const dispatch = createEventDispatcher();

    export let parent = null;
    export let container = undefined;
    export let options = [];
    export let noOptionsMessage = sgettext('$$OPTIONS$$');
    export let filterText = '';
    export let showDescriptions = true;
    export let showImages = true;
    export let showTooltip = true;
    export let selectedBackgroundColor;
    export let selectedFontColor;
    export let hoverBackgroundColor;
    export let hoverFontColor;
    export let centerItems = false;

    // data getters
    export let getItemValue = () => {};
    export let getItemLabel = () => {};
    export let getItemDescription = () => {};
    export let getItemImageUrl = () => {};

    // selected item and value
    export let selectedItem = null;
    let selectedItemIndex = 0;
    let value = null;
    $: value = getItemValue(selectedItem);
    let isItemSelected = item => getItemValue(item) === value;
    let handleSelect = (item, index) => {
        selectedItem = Object.assign({}, item);
        selectedItemIndex = index;
        dispatch('itemSelected', item);
    }

    // scrolling
    let isScrollingTimer = 0;
    let isScrolling = false;

    // hover
    let hoverItemIndex = -1;
    let handleHover = (i) => {
        if (isScrolling) return;
        hoverItemIndex = i;
    }
    async function updateHoverItem(increment) {
        if (increment > 0 && hoverItemIndex === options.length - 1) {
            hoverItemIndex = 0;
        } else if (increment < 0 && hoverItemIndex === 0) {
            hoverItemIndex = options.length - 1;
        } else {
            hoverItemIndex = hoverItemIndex + increment;
        }

        await tick();

        scrollToActiveItem('hover');
    }

    onMount(() => {
        if (options.length > 0 && value) {
            const _hoverItemIndex = options.findIndex(
                (item) => getItemValue(item) === value
            );
            if (_hoverItemIndex) hoverItemIndex = _hoverItemIndex;
        }

        scrollToActiveItem('active');

        container.addEventListener(
            'scroll',
            () => {
                clearTimeout(isScrollingTimer);

                isScrollingTimer = setTimeout(() => {
                    isScrolling = false;
                }, 100);
            },
            false
        );
    });

    // handlers
    let handleClick = args => {
        const { option, i, event } = args;
        event.stopPropagation();
        if (isItemSelected(option)) return closeList();
        hoverItemIndex = i;
        handleSelect(option, i);
    }
    function closeList() {
        dispatch('closeList');
    }

    function handleKeyDown(e) {
        const hoverItem = options[hoverItemIndex];
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                closeList();
                break;
            case 'ArrowDown':
                e.preventDefault();
                options.length && updateHoverItem(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                options.length && updateHoverItem(-1);
                break;
            case 'Enter':
                e.preventDefault();
                if (options.length === 0) break;
                if (isItemSelected(hoverItem)) {
                    closeList();
                    break;
                }
                selectedItemIndex = hoverItemIndex;
                handleSelect(options[hoverItemIndex], selectedItemIndex);
                break;
            case 'Tab':
                e.preventDefault();
                if (options.length === 0 || isItemSelected(hoverItem))
                    return closeList();
                selectedItemIndex = hoverItemIndex;
                handleSelect(options[hoverItemIndex], selectedItemIndex);
                break;
        }
    }

    function scrollToActiveItem(className) {
        if (!container) return;

        let offsetBounding;
        const focusedElemBounding = container.querySelector(
            `.option .${className}`
        );

        if (focusedElemBounding) {
            offsetBounding =
                container.getBoundingClientRect().bottom -
                focusedElemBounding.getBoundingClientRect().bottom;
        }

        container.scrollTop -= offsetBounding;
    }

    function isItemHover(hoverItemIndex, itemIndex) {
        return hoverItemIndex === itemIndex || options.length === 1;
    }

    export let placement = null;
    export let autoWidth = false;
    export let width = 550;
    export let height = 550;
    export let offset = 5;
    let style = `--height:${height}px;--width: ${width}px`;
    function computePlacement() {
        const parentWidth = parent.getBoundingClientRect().width;
        const parentHeight = parent.getBoundingClientRect().height;
        style = `--height:${height};`;
        if (autoWidth) width = parentWidth;
        style += `--width:${width}px;`;
        style += `min-width:${width}px;`;
        if (autoWidth) style += 'width:auto;';
        else style += `width:${width}px;`;
        if (
            placement === 'top' ||
            (placement === 'auto' && isOutOfViewport(parent).bottom)
        ) style += `bottom:${parentHeight + offset}px;`;
        else style += `top:${parentHeight + offset}px;`;
    }

    $: {
        if (parent && container) computePlacement();
    }
</script>

<svelte:window on:keydown={handleKeyDown} on:resize={computePlacement} />
<div class='container' bind:this={container} style={style}>
    {#each options as option, i}
        <div
            on:mouseover={() => handleHover(i)}
            on:focus={() => handleHover(i)}
            on:click={(event) => handleClick({ option, i, event })}
            class='option'
            tabindex='-1'
        >
            <OptionItem
                keyword={filterText}
                label={getItemLabel(option)}
                description={getItemDescription(option)}
                imageUrl={getItemImageUrl(option)}
                showDescription={showDescriptions}
                showImage={showImages}
                height=80
                imgHeight=50
                colImageWidth=50
                colLabelWidth=500
                colDescriptionWidth=400
                isActive={isItemSelected(option)}
                isHover={isItemHover(hoverItemIndex, i)}
                {selectedBackgroundColor}
                {selectedFontColor}
                {hoverBackgroundColor}
                {hoverFontColor}
                {showTooltip}
                {autoWidth}
                centerItem={centerItems}
            />
        </div>
    {:else}
        <div class='empty'>{noOptionsMessage}</div>
    {/each}
</div>

<style>
    .container {
        box-shadow: 0 2px 3px 0 rgba(44, 62, 80, 0.24);
        border-radius: 4px;
        max-height: 250px;
        overflow-y: auto;
        background: #fff;
        border: none;
        position: absolute;
        z-index: 99999;
        width: var(--width);
        left: 0;
        right: 0;
        overflow-x: hidden;
        padding: 0;
    }

    .option {
        width: 100%;
        padding: 0;
        margin: 0;
    }

    .empty {
        width: 100%;
        text-align: center;
        padding: 20px 0;
        color: #78848f;
    }
</style>
